/**
 * SidebarChat.tsx
 *
 * AI chat panel with:
 *  - On-demand page summary (button press — never automatic)
 *  - Rich markdown rendering (bold, lists, stats, headers)
 *  - Indigo/purple gradient design
 *
 * Nothing here calls the AI until the user asks. The summary used to fire on
 * mount, on every route change and on every record click, which is what made
 * it look like the assistant "runs on page load". Chat state lives in
 * aiSidebarStore so it survives collapsing the sidebar and is shared by the
 * desktop and mobile instances that AISidebar mounts.
 */
import { useState, useRef, useEffect, useCallback, type ReactNode } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Send, Loader2, Sparkles, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  chatWithContext,
  chatWithPageContext,
  type ContextType,
  type ChatMessage,
  type PageContextInfo,
} from '@/services/ai-context.service';
import { useAISidebarStore, buildContextKey, selectSession } from '@/store/aiSidebarStore';
import { MarkdownContent } from './MarkdownContent';
import { AssistantWelcome } from './AssistantWelcome';

interface SidebarChatProps {
  contextType?: ContextType | null;
  contextId?: number | null;
  pageContext?: PageContextInfo | null;
  /** Rendered inside the chat scroll area above the page summary (e.g. ContextPanel). */
  topContent?: ReactNode;
}

// ─── Page summary card ────────────────────────────────────────────────────────

function PageSummaryCard({
  summary,
  isLoading,
  onRefresh,
}: {
  summary: string | null;
  isLoading: boolean;
  onRefresh: () => void;
}) {
  const [expanded, setExpanded] = useState(true);

  return (
    <div className="mx-3 my-3 rounded-xl overflow-hidden border border-indigo-100 shadow-sm">
      {/* Gradient header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-3 py-2 flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Sparkles className="h-3.5 w-3.5 text-white/90" />
          <span className="text-xs font-semibold text-white tracking-wide">PAGE SUMMARY</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={onRefresh}
            disabled={isLoading}
            className="p-0.5 rounded text-white/70 hover:text-white transition-colors disabled:opacity-40"
            title="Refresh summary"
          >
            <RefreshCw className={cn('h-3 w-3', isLoading && 'animate-spin')} />
          </button>
          <button
            onClick={() => setExpanded((e) => !e)}
            className="p-0.5 rounded text-white/70 hover:text-white transition-colors"
          >
            {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          </button>
        </div>
      </div>

      {/* Content */}
      {expanded && (
        <div className="bg-white px-3 py-2.5">
          {isLoading ? (
            <div className="flex items-center gap-2 text-slate-400 py-1">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              <span className="text-xs">Analyzing page data...</span>
            </div>
          ) : summary ? (
            <div className="text-sm">
              <MarkdownContent content={summary} />
            </div>
          ) : (
            <p className="text-xs text-slate-400 italic">No summary available</p>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

/** How close to the bottom still counts as "following the conversation", in px. */
const NEAR_BOTTOM_PX = 48;

export function SidebarChat({ contextType, contextId, pageContext, topContent }: SidebarChatProps) {
  const [input, setInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  // Whether the user was parked at the bottom before the last resize. Updated on
  // scroll, read by the ResizeObserver below.
  const nearBottomRef = useRef(true);

  const hasEntity = contextType && contextId;
  const contextKey = buildContextKey(pageContext?.route, contextType, contextId);

  const session = useAISidebarStore((s) => selectSession(s, contextKey));
  const setSummary = useAISidebarStore((s) => s.setSummary);
  const appendMessages = useAISidebarStore((s) => s.appendMessages);
  const startSession = useAISidebarStore((s) => s.startSession);

  const { summary: pageSummary, messages, started } = session;
  const hasContext = Boolean(pageContext?.route || hasEntity);

  const callAI = (question: string, history: ChatMessage[]) => {
    if (pageContext) {
      return chatWithPageContext({
        question,
        pageContext,
        entityContext: hasEntity ? { contextType: contextType!, contextId: contextId! } : undefined,
        history,
      });
    }
    if (hasEntity) {
      return chatWithContext({ contextType: contextType!, contextId: contextId!, question, history });
    }
    return Promise.resolve("I don't have enough context to help right now.");
  };

  // On-demand page summary. `key` is captured at mutate time so a response that
  // arrives after the user has navigated away lands on the session it belongs
  // to instead of overwriting the new page's summary.
  const summaryMutation = useMutation({
    mutationFn: (_vars: { key: string }) =>
      callAI(
        hasEntity
          ? `Summarize this ${contextType} in 3-5 concise bullet points. Include the most important facts, any notable numbers or values, and one actionable insight. Use **bold** for key values.`
          : `Give me a concise summary of what's on this page right now. Include key counts/totals, highlight anything notable or unusual, and surface 2-3 interesting data points. Use **bold** for numbers and important values. Use bullet points for the data points.`,
        [],
      ),
    onSuccess: (answer, { key }) => setSummary(key, answer),
    onError: (err: unknown) => setError(errorText(err)),
  });

  // Chat mutation
  const chatMutation = useMutation({
    mutationFn: ({ question, history }: { question: string; history: ChatMessage[]; key: string }) =>
      callAI(question, history),
    onSuccess: (answer, { question, key }) => {
      appendMessages(key, [
        { role: 'user', content: question },
        { role: 'assistant', content: answer },
      ]);
    },
    onError: (err: unknown) => setError(errorText(err)),
  });

  // Clear the draft input and any stale error when switching page/record. This
  // deliberately does NOT fetch anything — that is the whole point of the change.
  useEffect(() => {
    setInput('');
    setError(null);
  }, [contextKey]);

  // Scroll this component's own container, rather than calling scrollIntoView on the
  // trailing sentinel. scrollIntoView also scrolls every ANCESTOR scroller it finds,
  // and inside the fixed mobile overlay that can move the document and drag the panel
  // out of position — the exact failure this chat was reported for.
  const scrollToBottom = useCallback((smooth: boolean) => {
    const el = scrollRef.current;
    if (!el) return;
    if (typeof el.scrollTo === 'function') {
      el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
      return;
    }
    el.scrollTop = el.scrollHeight;
  }, []);

  // Auto-scroll
  useEffect(() => {
    scrollToBottom(true);
  }, [messages, chatMutation.isPending, scrollToBottom]);

  // Re-pin when the container itself resizes, which on a phone is the on-screen
  // keyboard opening or closing. A shrinking container keeps its scrollTop, so without
  // this the newest exchange slides out of view exactly as the user starts typing.
  //
  // A ResizeObserver rather than a visualViewport listener or a focus handler: it fires
  // for every cause — keyboard, rotation, the panel changing width — with no guess at
  // the keyboard's animation duration, and it keeps this component ignorant of how the
  // panel around it is sized.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(() => {
      // Never yank someone who has scrolled up to read history.
      if (nearBottomRef.current) scrollToBottom(false);
    });
    observer.observe(el);

    return () => observer.disconnect();
  }, [scrollToBottom]);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    nearBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight <= NEAR_BOTTOM_PX;
  };

  const runSummary = () => {
    if (!hasContext || summaryMutation.isPending) return;
    setError(null);
    startSession(contextKey);
    summaryMutation.mutate({ key: contextKey });
  };

  // Re-runs the summary in place. Deliberately leaves the chat history alone —
  // the card shows its own spinner while the replacement is generated.
  const refreshSummary = () => {
    if (summaryMutation.isPending) return;
    setError(null);
    summaryMutation.mutate({ key: contextKey });
  };

  const ask = (question: string) => {
    const q = question.trim();
    if (!q || chatMutation.isPending) return;
    setError(null);
    setInput('');
    startSession(contextKey);
    chatMutation.mutate({ question: q, history: [...messages], key: contextKey });
  };

  const handleSend = () => ask(input);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const placeholder = hasEntity
    ? `Ask about this ${contextType}...`
    : pageContext
      ? `Ask about ${pageContext.pageName}...`
      : 'Ask me anything...';

  // Greeting-first: show the welcome screen until the user actually asks for
  // something. `started` also covers the case where a request is in flight.
  const showWelcome = !started && !pageSummary && messages.length === 0;
  const showSummaryCard = Boolean(pageSummary) || summaryMutation.isPending;

  return (
    <div className="flex flex-col h-full min-h-0 bg-slate-50/30">
      {/* Scrollable content — takes remaining height so input is always at the bottom */}
      <div ref={scrollRef} onScroll={handleScroll} className="flex-1 min-h-0 overflow-y-auto">
        {topContent}

        {showWelcome ? (
          <AssistantWelcome
            pageContext={pageContext}
            contextType={contextType}
            isSummarizing={summaryMutation.isPending}
            onSummarize={runSummary}
            onPick={ask}
            disabled={!hasContext || chatMutation.isPending}
          />
        ) : (
          showSummaryCard && (
            <PageSummaryCard
              summary={pageSummary}
              isLoading={summaryMutation.isPending}
              onRefresh={refreshSummary}
            />
          )
        )}

        {messages.length > 0 && (
          <div className="flex items-center gap-2 px-3 py-1">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">Chat</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>
        )}

        <div className="px-3 pt-1 pb-2 space-y-3">
          {messages.map((msg, i) => (
            <div
              key={i}
              className={cn('flex', msg.role === 'user' ? 'justify-end' : 'justify-start')}
            >
              {msg.role === 'user' ? (
                <div className="max-w-[85%] rounded-2xl rounded-tr-sm px-3 py-2 bg-gradient-to-br from-indigo-600 to-indigo-700 text-white text-sm shadow-sm">
                  {msg.content}
                </div>
              ) : (
                <div className="max-w-[95%] rounded-2xl rounded-tl-sm bg-white border border-slate-200 px-3 py-2.5 shadow-sm">
                  <MarkdownContent content={msg.content} />
                </div>
              )}
            </div>
          ))}

          {chatMutation.isPending && (
            <div className="flex justify-start">
              <div className="bg-white border border-slate-200 rounded-2xl rounded-tl-sm px-3 py-2 text-sm text-slate-400 flex items-center gap-2 shadow-sm">
                <span className="flex gap-1">
                  <span className="h-1.5 w-1.5 rounded-full bg-indigo-400 animate-bounce [animation-delay:0ms]" />
                  <span className="h-1.5 w-1.5 rounded-full bg-indigo-400 animate-bounce [animation-delay:150ms]" />
                  <span className="h-1.5 w-1.5 rounded-full bg-indigo-400 animate-bounce [animation-delay:300ms]" />
                </span>
                <span className="text-xs">Thinking...</span>
              </div>
            </div>
          )}

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
              {error}
            </div>
          )}

          {/* Load-bearing, despite looking inert. It is the last child of a `space-y-3`
              list, so deleting it removes 12px of trailing space from the desktop rail
              as well as the overlay. It no longer receives a scrollIntoView — see
              scrollToBottom above for why that call had to go. */}
          <div ref={chatEndRef} />
        </div>
      </div>

      {/*
        Input bar — anchored to the bottom of the sidebar at every screen size.

        This component renders inside BOTH the desktop rail and the mobile overlay, so
        every addition below is `max-md:`-prefixed. Tailwind emits those inside
        `@media not all and (min-width: 768px)`, which leaves the desktop rail's computed
        style byte-for-byte what it was. Do not lift a prefix to "fix it everywhere".
      */}
      <div className="shrink-0 px-3 py-2.5 border-t border-slate-200 bg-white">
        <div className="flex items-center gap-2">
          {/*
            No font-size utility here on purpose. This input is 14px, which is under the
            threshold that makes mobile Safari zoom the page on focus — and that zoom is
            what slid the panel's right edge, and the Send button on it, off-screen. It
            is fixed globally by the auto-zoom floor at the bottom of `index.css`
            (SCRUM-1373), which covers every control up to 1023.98px on a coarse pointer.
            A local `max-md:text-base` would be redundant *and* would lose to that rule
            on specificity, so it would read as the fix while doing nothing.

            `max-md:min-w-0` is defensive only. At 375px the row has ~319px available
            against a ~234px minimum, so there is no flex overflow to fix today; this
            just stops a longer control from creating one later.

            `touch-target-h` is a min-height rather than the taller padding it replaces,
            so `py-1.5` still describes the resting size and only the floor moves.
          */}
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={chatMutation.isPending}
            className="flex-1 text-sm bg-slate-50 border border-slate-200 rounded-xl px-3 py-1.5 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 disabled:opacity-50 transition-colors max-md:min-w-0 touch-target-h"
          />
          {/*
            26px target before this. The utilities come from index.css rather than being
            `max-md:` variants: this composer renders inside the `lg:hidden` overlay too,
            which is still the visible instance at 800px, where a `max-md:` target went
            back to 26px on a device that is all touch. Their gate is that whole band.
          */}
          <button
            type="button"
            onClick={handleSend}
            disabled={!input.trim() || chatMutation.isPending}
            className="rounded-xl bg-gradient-to-br from-indigo-600 to-purple-600 p-1.5 text-white hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed shadow-sm touch-target-44"
          >
            <Send className="h-3.5 w-3.5 touch-target-icon" />
          </button>
        </div>
      </div>
    </div>
  );
}

function errorText(err: unknown): string {
  if (err instanceof Error && err.message) return err.message;
  return 'Something went wrong. Please try again.';
}
